import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useInbox } from "@/context/InboxContext";
import { Button } from "@/components/ui/button";
import { AtSign, UserCheck, Check, Undo2, Inbox as InboxIcon, ListChecks, Loader2 } from "lucide-react";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function EventIcon({ event }) {
  if (event === "assigned")
    return <span className="h-8 w-8 rounded-full bg-[#EAF3EC] text-[#0B3D3D] flex items-center justify-center shrink-0"><UserCheck className="h-4 w-4" /></span>;
  return <span className="h-8 w-8 rounded-full bg-[#F5EEDD] text-[#8A6D1F] flex items-center justify-center shrink-0"><AtSign className="h-4 w-4" /></span>;
}

export default function Inbox() {
  const [tab, setTab] = useState("active");
  const [items, setItems] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { setCount, refresh } = useInbox();

  const load = useCallback((t) => {
    setLoading(true);
    api.get(`/inbox?tab=${t}`)
      .then((r) => { setItems(r.data.items); setActiveCount(r.data.active_count); setCount(r.data.active_count); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [setCount]);

  useEffect(() => { load(tab); }, [tab, load]);

  const clearItem = async (e, n) => {
    e.stopPropagation();
    await api.post(`/inbox/${n.id}/clear`).catch(() => {});
    setItems((prev) => prev.filter((x) => x.id !== n.id));
    setActiveCount((c) => Math.max(0, c - 1));
    setCount((c) => Math.max(0, c - 1));
    toast.success("Cleared");
  };
  const restoreItem = async (e, n) => {
    e.stopPropagation();
    await api.post(`/inbox/${n.id}/unclear`).catch(() => {});
    setItems((prev) => prev.filter((x) => x.id !== n.id));
    setActiveCount((c) => c + 1);
    setCount((c) => c + 1);
    toast.success("Moved back to Active");
  };
  const clearAll = async () => {
    await api.post("/inbox/clear-all").catch(() => {});
    setItems([]);
    setActiveCount(0);
    setCount(0);
    toast.success("Inbox cleared");
  };
  const openItem = (n) => { if (n.shipment_id) navigate(`/shipments/${n.shipment_id}`); };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="inbox-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <span className="h-11 w-11 rounded-xl bg-[#0B3D3D] text-[#C5A253] flex items-center justify-center"><InboxIcon className="h-5 w-5" /></span>
          <div>
            <h1 className="font-heading text-2xl font-extrabold text-[#111411] tracking-tight">Inbox</h1>
            <p className="text-sm text-muted-foreground">Mentions and assignments that need your attention</p>
          </div>
        </div>
        {tab === "active" && activeCount > 0 && (
          <Button variant="outline" size="sm" onClick={clearAll} data-testid="inbox-clear-all" className="gap-2">
            <ListChecks className="h-4 w-4" /> Clear all
          </Button>
        )}
      </div>

      <div className="flex items-center gap-1 border-b border-border">
        <button data-testid="inbox-tab-active" onClick={() => setTab("active")}
                className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === "active" ? "border-[#0B3D3D] text-[#0B3D3D]" : "border-transparent text-muted-foreground hover:text-[#111411]"}`}>
          Active {activeCount > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#C24127] text-white text-[10px] font-bold align-middle">{activeCount}</span>}
        </button>
        <button data-testid="inbox-tab-cleared" onClick={() => setTab("cleared")}
                className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === "cleared" ? "border-[#0B3D3D] text-[#0B3D3D]" : "border-transparent text-muted-foreground hover:text-[#111411]"}`}>
          Cleared
        </button>
      </div>

      <div className="rounded-lg border border-border bg-white overflow-hidden" data-testid="inbox-list">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16" data-testid="inbox-empty">
            <span className="mx-auto h-12 w-12 rounded-full bg-[#EAF3EC] text-[#0B3D3D] flex items-center justify-center mb-3"><Check className="h-6 w-6" /></span>
            <p className="text-sm font-medium text-[#111411]">{tab === "active" ? "You're all caught up" : "Nothing cleared yet"}</p>
            <p className="text-xs text-muted-foreground mt-1">{tab === "active" ? "New mentions and assignments will show up here." : "Items you clear will appear here."}</p>
          </div>
        ) : (
          items.map((n) => (
            <div key={n.id} data-testid={`inbox-item-${n.id}`} onClick={() => openItem(n)}
                 className="group flex items-start gap-3 px-4 py-3.5 border-b border-border last:border-0 hover:bg-[#F9F9F8] cursor-pointer transition-colors">
              <EventIcon event={n.event} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-[#111411] truncate">{n.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>
                <p className="text-[11px] text-muted-foreground mt-1">{timeAgo(n.created_at)}{n.order_id ? ` · Order #${n.order_id}` : ""}</p>
              </div>
              {tab === "active" ? (
                <Button size="sm" onClick={(e) => clearItem(e, n)} data-testid={`inbox-clear-${n.id}`}
                        className="gap-1.5 bg-[#1FA855] hover:bg-[#178a45] text-white shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Check className="h-3.5 w-3.5" /> Clear
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={(e) => restoreItem(e, n)} data-testid={`inbox-restore-${n.id}`}
                        className="gap-1.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Undo2 className="h-3.5 w-3.5" /> Restore
                </Button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
